/* eslint-disable no-nested-ternary */
import { memo, useState, useCallback } from 'react';
import Iconify from '@emfood/shared/src/components/iconify';
import { useAuthContext } from '@emfood/shared';

import { LoadingButton } from '@mui/lab';
import { Box, Stack, Tooltip, Divider, IconButton } from '@mui/material';

import { DetailsFavouritesCollectionModel, FavouritesDataModel } from 'src/api/models/favourites';

import AddToCartButton from 'src/components/products/add-to-cart-button';
import UpdateProductButton from 'src/components/products/update-product-button';

import AddProductToFavouritesCollectionDialog from './add-product-to-favourites.collection-dialog';
import AddProductToFavouritesCateringManagerDialog from './add-product-to-favourites-catering-manager-dialog';

export type ActiveOperationType = 'add' | 'remove' | 'update';
// Keyed by row id (productStockId). A row is present iff it has an in-flight mutation.
// Each row's buttons depend only on its OWN entry — no cross-row disable, no flash
// on sibling rows when another row mutates. Concurrent updates are safe because
// `handleUpdate` uses a functional setQueryData that patches a single cartItem by id.
// Add/Remove do wholesale cart replacement and leave a small race window against
// concurrently-resolving writes on other rows; react-query invalidation on window
// focus / next fetch corrects any transient staleness.
export type ActiveOperations = Map<string, ActiveOperationType>;

interface ActionButtonProps {
  params: any;
  handleUpdate: any;
  handleAddToCart: any;
  handleRemoveFromCart: any;
  activeOperations: ActiveOperations;
}
const ActionButton = ({
  params,
  handleUpdate,
  handleAddToCart,
  handleRemoveFromCart,
  activeOperations,
}: ActionButtonProps) => {
  const thisRowOp = activeOperations.get(params.row.productStockId);
  const isThisRowMutating = thisRowOp !== undefined;
  const { isInCart } = params.row;

  const isSyncedWithCart =
    isInCart && params.row.quantity.toString() === params.row.cartQuantity.toString();
  // Update is disabled when there's nothing to update, or this row itself is already
  // mid-mutation (spam-click guard). Mutations on other rows do not affect this row.
  const isUpdateDisabled = isSyncedWithCart || isThisRowMutating;

  // Cart-action area takes all available space to the left of the favourite icon.
  // When in basket: Update + Remove cluster to the right edge of that area.
  // When not in basket: Add is centred horizontally inside the area.
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        // No gap between Update and Remove — each IconButton has its own internal
        // padding which gives enough visual separation, and we need the saved space
        // for the in-cart trio to fit the narrow actions column.
        justifyContent: isInCart ? 'flex-end' : 'center',
      }}
    >
      {isInCart ? (
        <>
          <UpdateProductButton
            isDisabled={isUpdateDisabled}
            isInCart={isSyncedWithCart}
            isLoading={thisRowOp === 'update'}
            onClick={() =>
              handleUpdate(params.row.cartItemId, params.row.quantity, params.row.productStockId)
            }
            quantity={params.row.quantity}
            maxQuantity={params.row.surplusEntries?.reservedQuantity}
          />
          <Tooltip title="Remove from basket">
            <IconButton
              disabled={isThisRowMutating}
              onClick={() =>
                handleRemoveFromCart(params.row.cartItemId, params.row.productStockId)
              }
              aria-label="Remove from basket"
            >
              {thisRowOp === 'remove' ? (
                <Iconify icon="eos-icons:loading" />
              ) : (
                <Iconify icon="solar:trash-bin-trash-bold" />
              )}
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <AddToCartButton
          isLoading={thisRowOp === 'add'}
          isDisabled={false}
          onClick={() =>
            handleAddToCart(
              params.row.productStockId,
              params.row.quantity,
              params.row.productStockId
            )
          }
          quantity={params.row.quantity}
          maxQuantity={params.row.surplusEntries?.reservedQuantity}
        />
      )}
    </Box>
  );
};

interface ActionCellProps {
  params: any;
  handleUpdate: any;
  handleAddToCart: any;
  handleRemoveFromCart: any;
  activeOperations: ActiveOperations;
  personalFavouritesCollections?: DetailsFavouritesCollectionModel[];
  favouritesCollectionsData?: FavouritesDataModel;
  isFavouritesLoading?: boolean;
  favouritesCollectionsError?: unknown;
}

export const ActionsCell = memo(
  ({
    params,
    handleAddToCart,
    handleUpdate,
    handleRemoveFromCart,
    activeOperations,
    personalFavouritesCollections,
    favouritesCollectionsData,
    isFavouritesLoading,
    favouritesCollectionsError,
  }: ActionCellProps) => {
    const { user } = useAuthContext();
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const isCateringManager =
      user?.roles?.some((role: any) => role.name === 'Catering Manager') || false;

    const handleOpenFavouritesDialog = useCallback(() => {
      setIsDialogOpen(true);
    }, []);

    const handleCloseFavouritesDialog = () => {
      setIsDialogOpen(false);
    };

    return (
      <>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          divider={<Divider orientation="vertical" sx={{ height: 20 }} />}
          sx={{
            width: '100%',
            // Theme defaults IconButton to size="large" (48px). The actions column is
            // fixed at 160px (144px under 1467px), so the in-cart trio (Update + Remove + Heart)
            // needs tighter padding to fit without clipping.
            '& .MuiIconButton-root': {
              padding: '8px',
              fontSize: '1.5rem',
            },
            '@media (max-width:1467px)': {
              gap: 0.7,
              '& .MuiIconButton-root': {
                padding: '7.2px',
                fontSize: '1.35rem',
              },
            },
          }}
        >
          <ActionButton
            params={params}
            handleAddToCart={handleAddToCart}
            handleUpdate={handleUpdate}
            handleRemoveFromCart={handleRemoveFromCart}
            activeOperations={activeOperations}
          />
          <Tooltip title="Add to favourites">
            <IconButton onClick={handleOpenFavouritesDialog} aria-label="Add to Favourites">
              <Iconify
                icon={params.row.hasLinkedFavouritesLists ? 'eva:heart-fill' : 'eva:heart-outline'}
              />
            </IconButton>
          </Tooltip>
        </Stack>

        {isCateringManager && favouritesCollectionsData ? (
          <AddProductToFavouritesCateringManagerDialog
            open={isDialogOpen}
            onClose={handleCloseFavouritesDialog}
            favouritesCollectionsData={favouritesCollectionsData}
            isFavouritesLoading={isFavouritesLoading}
            favouritesCollectionsError={favouritesCollectionsError}
            productStockId={params.row.productStockId}
            preSelectedFavourites={params.row.favouritesCollectionIds}
          />
        ) : (
          <AddProductToFavouritesCollectionDialog
            open={isDialogOpen}
            onClose={handleCloseFavouritesDialog}
            personalFavouritesCollections={personalFavouritesCollections}
            sharedFavouritesCollections={[
              ...(favouritesCollectionsData?.orgShared ?? []),
              ...(favouritesCollectionsData?.siteShared ?? []),
            ]}
            isFavouritesLoading={isFavouritesLoading}
            favouritesCollectionsError={favouritesCollectionsError}
            productStockId={params.row.productStockId}
            preSelectedFavourites={params.row.favouritesCollectionIds}
          />
        )}
      </>
    );
  }
);
